import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  FolderOpen,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Program {
  id: number;
  agency_id: number;
  category_id: number | null;
  name: string;
  is_active: number;
  category_name?: string;
}

interface ProgramCategory {
  id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProgramsList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [formName, setFormName] = useState("");
  const [formCategoryId, setFormCategoryId] = useState("");
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState("");

  const { data: programs, isLoading } = useQuery<Program[]>({
    queryKey: ["admin-programs"],
    queryFn: async () => {
      const res = await api.get<{ data: Program[] }>("/admin/programs");
      return res.data;
    },
  });

  const { data: categories } = useQuery<ProgramCategory[]>({
    queryKey: ["program-categories"],
    queryFn: async () => {
      const res = await api.get<{ data: ProgramCategory[] }>(
        "/admin/programs/categories/list",
      );
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; categoryId?: number }) =>
      api.post("/admin/programs", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      name?: string;
      categoryId?: number | null;
      isActive?: boolean;
    }) => api.put(`/admin/programs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/programs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      api.post("/admin/programs/categories", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["program-categories"] });
      setShowCategoryForm(false);
      setCategoryName("");
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditingProgram(null);
    setFormName("");
    setFormCategoryId("");
  }

  function handleEdit(program: Program) {
    setEditingProgram(program);
    setFormName(program.name);
    setFormCategoryId(program.category_id ? String(program.category_id) : "");
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const catId = formCategoryId ? parseInt(formCategoryId, 10) : undefined;
    if (editingProgram) {
      updateMutation.mutate({
        id: editingProgram.id,
        name: formName,
        categoryId: catId ?? null,
      });
    } else {
      createMutation.mutate({ name: formName, categoryId: catId });
    }
  }

  function toggleActive(program: Program) {
    updateMutation.mutate({
      id: program.id,
      isActive: !program.is_active,
    });
  }

  // Group programs by category
  const grouped = new Map<string, Program[]>();
  for (const p of programs ?? []) {
    const key = p.category_name ?? "Uncategorized";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(p);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Programs</h1>
          <p className="text-sm text-muted-foreground">
            Manage funded programs and program categories
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCategoryForm(!showCategoryForm)}
          >
            Add Category
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            New Program
          </Button>
        </div>
      </div>

      {/* Category form */}
      {showCategoryForm && (
        <Card>
          <CardContent className="p-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createCategoryMutation.mutate({ name: categoryName });
              }}
              className="flex items-end gap-3"
            >
              <div className="flex-1">
                <Input
                  label="Category Name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  placeholder="e.g. Federal, State, Foundation"
                />
              </div>
              <Button
                type="submit"
                size="sm"
                loading={createCategoryMutation.isPending}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCategoryForm(false)}
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Program form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingProgram ? "Edit Program" : "New Program"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Program Name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  placeholder="e.g. VOCA, VAWA, State DV Grant"
                />
                <div className="space-y-1.5">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Category
                  </label>
                  <select
                    value={formCategoryId}
                    onChange={(e) => setFormCategoryId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No category</option>
                    {(categories ?? []).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {((createMutation.error ?? updateMutation.error) as Error)
                    ?.message ?? "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  loading={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingProgram ? "Save Changes" : "Create Program"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* Empty state */}
      {!isLoading && (programs ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No programs configured yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Programs grouped by category */}
      {!isLoading &&
        Array.from(grouped.entries()).map(([categoryName, progs]) => (
          <Card key={categoryName}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {categoryName}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Program Name
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {progs.map((program) => (
                    <tr
                      key={program.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">{program.name}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(program)}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                            program.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600",
                          )}
                        >
                          {program.is_active ? (
                            <>
                              <CheckCircle className="h-3 w-3" /> Active
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3" /> Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(program)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (
                                confirm(
                                  "Delete this program? This cannot be undone.",
                                )
                              ) {
                                deleteMutation.mutate(program.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
